import { useMemo, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import Typography from '@mui/material/Typography';
import {
  Button,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  useTheme
} from '@mui/material';
import { useAppDispatch, useAppSelector } from 'src/app/hooks';
import { Box } from '@mui/system';
import CloseIcon from '@mui/icons-material/Close';
import AddKeywordOrHastagRetail from '../ModalManageObjectRetail/components/AddKeywordOrHashtagRetail';
import InputCardSocialProfile from '../ModalManageObjectRetail/components/InputCardSocialProfile';
import { IObject } from 'src/models/objectManagement';
import {
  reducerRemoveTempKeywordOrHashtag,
  reducerUpdateAllTempKeywordHashtag
} from 'src/features/objectManagement';
import useOnboarding from 'src/services/useOnboarding';
import Confirmation from 'src/components/Confirmation';
import {
  GET_OBJECTS_WITH_ENTITY,
  GET_OBJECTS_WITH_STATUS
} from 'src/services/graphql/objectManagement/query';
import useObjectManagement from 'src/services/useObjectManagement';
import useUsers from 'src/services/useUsers';
import { useApolloClient } from '@apollo/client';
import NotAllowedTooltip from 'src/components/NotAllowedTooltip';
import DataCollectionInformation from 'src/components/DataCollectionInformation';

const ModalAddKeywordAndHashtagRetail = ({
  streamType,
  buttonVariant,
  showButton,
  handleCloseModal,
  onDone
}: {
  streamType: 'keyword' | 'hashtag' | null;
  buttonVariant: 'outlined' | 'contained';
  showButton: boolean;
  handleCloseModal: () => void;
  onDone?: () => void;
}) => {
  const [open, setOpen] = useState(showButton ? false : true);
  const dispatch = useAppDispatch();

  const handleClickOpen = () => {
    dispatch(reducerUpdateAllTempKeywordHashtag([]));
    setOpen(true);
  };

  const handleClose = () => {
    handleCloseModal();
    setOpen(false);
    dispatch(reducerUpdateAllTempKeywordHashtag([]));
  };

  const theme = useTheme();

  const tempInputKeywordOrHashtag = useAppSelector(
    (state) => state.objectManagement.tempInputKeywordOrHashtag
  );

  const {
    onBoarding: { advanceObjectQuota, advanceObjectQuotaUsed },
    id: projectId,
    role: userRole
  } = useAppSelector((state) => state.users.project);

  const { createObjects, responseCreateObjects } = useOnboarding();
  const { getObjectList } = useObjectManagement();
  const { getProjectDetailResult } = useUsers();
  const client = useApolloClient();

  const objectList = useAppSelector(
    (state) => state.objectManagement.objectList.data
  );
  const objectListKeywordAndHashtag = useMemo((): Array<IObject> => {
    return objectList.filter(
      (object) => object.streamType === 'keyword' && !object.isHashtag
    );
  }, [objectList]);

  const [openAddConfirmation, setOpenAddConfirmation] =
    useState<boolean>(false);

  const handleRemove = (id: string) => {
    dispatch(reducerRemoveTempKeywordOrHashtag(id));
  };

  const isObjectAlreadyIncluded = () => {
    return objectListKeywordAndHashtag.some((object) =>
      tempInputKeywordOrHashtag.find(
        (item) =>
          object.objectName === item.objectName &&
          object.socialMedia === item.socialMedia &&
          object.isHashtag === item.isHashtag
      )
    );
  };

  const handleSubmit = () => {
    const keywordPayload = {
      projectId: projectId,
      streamType: 'keyword',
      objectInput: [
        ...tempInputKeywordOrHashtag.map(({ streamType, id, ...rest }) => ({
          ...rest,
          content: rest.isHashtag ? `#${rest.objectName}` : rest.objectName,
          objectName: rest.isHashtag ? `#${rest.objectName}` : rest.objectName,
          languages: rest.languages !== '' ? [rest.languages] : []
        }))
      ]
    };
    createObjects({
      variables: keywordPayload
    }).then(() => {
      getObjectList(projectId);
      handleClose();
      getProjectDetailResult(projectId);
      client.refetchQueries({
        include: [GET_OBJECTS_WITH_ENTITY, GET_OBJECTS_WITH_STATUS]
      });
      if (onDone) {
        onDone();
      }
    });
  };

  const isMember = userRole === 'member';

  return (
    <>
      {showButton ? (
        isMember ? (
          <NotAllowedTooltip>
            <Button
              variant={buttonVariant}
              sx={
                buttonVariant === 'outlined'
                  ? {
                      color: theme.palette.primary.main,
                      border: `1px solid ${theme.palette.primary.main}`
                    }
                  : {
                      backgroundColor: theme.palette.primary.dark
                    }
              }
            >
              + Add{' '}
              {streamType !== null
                ? streamType.charAt(0).toUpperCase() + streamType.slice(1)
                : 'Keyword & Hashtag'}
            </Button>
          </NotAllowedTooltip>
        ) : (
          <Button
            variant={buttonVariant}
            onClick={handleClickOpen}
            sx={
              buttonVariant === 'outlined'
                ? {
                    color: theme.palette.primary.main,
                    border: `1px solid ${theme.palette.primary.main}`
                  }
                : {
                    backgroundColor: theme.palette.primary.dark
                  }
            }
          >
            + Add{' '}
            {streamType !== null
              ? streamType.charAt(0).toUpperCase() + streamType.slice(1)
              : 'Keyword & Hashtag'}
          </Button>
        )
      ) : null}
      <Dialog onClose={handleClose} open={open} fullWidth maxWidth={'lg'}>
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500]
          }}
        >
          <CloseIcon />
        </IconButton>
        <DialogTitle
          sx={{
            m: theme.spacing(3, 3, 0),
            p: theme.spacing(2, 3, 0, 3)
          }}
        >
          <Typography variant="h4">Add Keyword</Typography>
        </DialogTitle>
        <DialogContent
          sx={{
            m: { xs: 0, sm: theme.spacing(1, 3) }
          }}
        >
          <DataCollectionInformation />
          <Grid container spacing={2} sx={{ my: 1 }}>
            <Grid item lg={6}>
              <AddKeywordOrHastagRetail streamType={streamType} />
            </Grid>
            <Grid item lg={6}>
              <FormControl fullWidth size="medium">
                <InputCardSocialProfile
                  title={`Added Keyword (${
                    advanceObjectQuotaUsed + tempInputKeywordOrHashtag.length
                  } / ${advanceObjectQuota})`}
                  information="Tracked social media will appear here"
                  objectList={tempInputKeywordOrHashtag}
                  onRemove={handleRemove}
                />
              </FormControl>
            </Grid>
            <Grid item lg={12}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  gap: 1
                }}
              >
                <Button
                  variant="contained"
                  color="primary"
                  sx={{ width: theme.spacing(15), height: theme.spacing(5) }}
                  disabled={
                    responseCreateObjects.loading ||
                    tempInputKeywordOrHashtag.length === 0
                  }
                  onClick={() =>
                    isObjectAlreadyIncluded()
                      ? setOpenAddConfirmation(true)
                      : handleSubmit()
                  }
                >
                  Done
                </Button>
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
      </Dialog>
      <Confirmation
        title={'Add this object?'}
        labelButton={'Add'}
        message={`The keyword/hashtag
         you want to add has already been added.`}
        open={openAddConfirmation}
        onClose={() => setOpenAddConfirmation(false)}
        onOk={() => {
          handleSubmit();
        }}
      />
    </>
  );
};

export default ModalAddKeywordAndHashtagRetail;
